import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Fullscreen as FullscreenIcon, Dashboard as DashboardIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { useMode } from '../contexts/ModeContext';
import { RefreshIconButton } from '../components/RefreshIconButton';

// Grafana URL for iframe embedding.
// IMPORTANT: default to same-origin `/grafana` so this works from any LAN machine
// (avoids hardcoding localhost:3001, which points at the *client* machine).
const GRAFANA_URL =
  process.env.REACT_APP_GRAFANA_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/grafana-embed` : 'http://localhost:3001');

interface Dashboard {
  uid: string;
  title: string;
  uri: string;
  type: string;
}

const GrafanaDashboards: React.FC = () => {
  const { mode } = useMode();
  const location = useLocation();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [selectedDashboard, setSelectedDashboard] = useState<string>('warehouse-safety');
  const [loadingDashboards, setLoadingDashboards] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('1h');
  const [tempCalcFn, setTempCalcFn] = useState('avg_over_time');
  const [tempCalcWindow, setTempCalcWindow] = useState('15m');
  const [refreshKey, setRefreshKey] = useState(0);

  // Predefined dashboards (fallback if API doesn't return any)
  const defaultDashboards = useMemo(
    () => [
      { uid: 'warehouse-safety', title: 'Warehouse Safety Overview' },
      { uid: 'ppe-monitoring', title: 'PPE Monitoring' },
      { uid: 'zone-analytics', title: 'Zone Analytics' },
      { uid: 'freight-ocr-pilot', title: 'Freight OCR (Pilot KPIs)' },
    ],
    []
  );

  // Fetch available dashboards
  useEffect(() => {
    const fetchDashboards = async () => {
      setLoadingDashboards(true);
      try {
        // Use the shared axios client so JWT/X-API-Key headers are included.
        const res = await api.get('/api/v1/grafana/dashboards', { timeout: 7000 });
        const data = res.data;
        if (Array.isArray(data)) setDashboards(data);
      } catch (err) {
        setError('Failed to fetch Grafana dashboards (auth or proxy issue). Using fallback list.');
      } finally {
        setLoadingDashboards(false);
      }
    };

    fetchDashboards();
  }, []);

  // If the default dashboard UID doesn't exist (or dashboards arrive after initial render),
  // pick the first available dashboard to avoid "Dashboard not found" + empty dropdown.
  useEffect(() => {
    const available = dashboards.length > 0 ? dashboards : defaultDashboards;
    if (!available || available.length === 0) return;
    const hasSelected = available.some((d: any) => d?.uid === selectedDashboard);
    if (!hasSelected) setSelectedDashboard(available[0].uid);
    // defaultDashboards is static, excluded to avoid unnecessary effect runs
  }, [dashboards, selectedDashboard, defaultDashboards]);

  // Build Grafana embed URL (direct access with anonymous auth)
  const getEmbedUrl = () => {
    const from = `now-${timeRange}`;
    const to = 'now';
    const params = new URLSearchParams({
      orgId: '1',
      from,
      to,
      theme: 'light',
      kiosk: '',
    });

    const qp = new URLSearchParams(location.search || '');
    const deviceId = String(
      qp.get('device') || qp.get('device_id') || qp.get('var-device_id') || ''
    ).trim();
    const cameraId = String(qp.get('camera_id') || qp.get('var-camera_id') || '').trim();
    if (deviceId) params.set('var-device_id', deviceId);
    if (cameraId) params.set('var-camera_id', cameraId);
    // Default to real configured zones instead of fallback buckets when opening scoped views.
    params.set('var-zone', '.*');

    if (selectedDashboard === 'warehouse-safety') {
      params.set('var-temp_calc_fn', tempCalcFn);
      params.set('var-temp_calc_window', tempCalcWindow);
    }

    return `${GRAFANA_URL}/d/${selectedDashboard}?${params.toString()}`;
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleFullscreen = () => {
    const iframe = document.getElementById('grafana-iframe') as HTMLIFrameElement;
    if (iframe) {
      iframe.requestFullscreen?.();
    }
  };

  const availableDashboards = dashboards.length > 0 ? dashboards : defaultDashboards;

  // Prefer OCR dashboard when in OCR mode (best-effort).
  useEffect(() => {
    if (mode !== 'ocr') return;
    const available = dashboards.length > 0 ? dashboards : defaultDashboards;
    const hasOcr = available.some((d: any) => d?.uid === 'freight-ocr-pilot');
    if (hasOcr) setSelectedDashboard('freight-ocr-pilot');
  }, [mode, dashboards, defaultDashboards]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ width: 'calc(100vw - 280px)', height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1} bgcolor="background.paper" borderBottom={1} borderColor="divider">
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" fontWeight={600}>
            📊 Metrics & Analytics
          </Typography>
          <Chip label="Real-time" color="primary" size="small" />
          {loadingDashboards && <Chip label="Loading dashboards…" size="small" variant="outlined" />}
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Dashboard</InputLabel>
            <Select
              value={selectedDashboard}
              label="Dashboard"
              onChange={(e) => setSelectedDashboard(e.target.value)}
            >
              {availableDashboards.map((d) => (
                <MenuItem key={d.uid} value={d.uid}>
                  {d.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Time</InputLabel>
            <Select
              value={timeRange}
              label="Time"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="15m">15m</MenuItem>
              <MenuItem value="1h">1h</MenuItem>
              <MenuItem value="6h">6h</MenuItem>
              <MenuItem value="24h">24h</MenuItem>
              <MenuItem value="7d">7d</MenuItem>
            </Select>
          </FormControl>

          {selectedDashboard === 'warehouse-safety' && (
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Temp Calc</InputLabel>
              <Select
                value={tempCalcFn}
                label="Temp Calc"
                onChange={(e) => setTempCalcFn(e.target.value)}
              >
                <MenuItem value="last_over_time">Last</MenuItem>
                <MenuItem value="avg_over_time">Average</MenuItem>
                <MenuItem value="max_over_time">Maximum</MenuItem>
                <MenuItem value="min_over_time">Minimum</MenuItem>
              </Select>
            </FormControl>
          )}

          {selectedDashboard === 'warehouse-safety' && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Temp Window</InputLabel>
              <Select
                value={tempCalcWindow}
                label="Temp Window"
                onChange={(e) => setTempCalcWindow(e.target.value)}
              >
                <MenuItem value="5m">5m</MenuItem>
                <MenuItem value="15m">15m</MenuItem>
                <MenuItem value="1h">1h</MenuItem>
                <MenuItem value="6h">6h</MenuItem>
                <MenuItem value="24h">24h</MenuItem>
              </Select>
            </FormControl>
          )}

          <RefreshIconButton onClick={handleRefresh} size="small" title="Reload embedded dashboard" />
          <IconButton onClick={handleFullscreen} size="small">
            <FullscreenIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Grafana Embed - Full Width */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
        <iframe
          id="grafana-iframe"
          key={`${refreshKey}:${location.search}`}
          src={getEmbedUrl()}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          title="Edgentik Metrics Dashboard"
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      </Box>

      {/* Dashboard Info - Slim Footer */}
      <Box px={2} py={0.5} display="flex" justifyContent="space-between" alignItems="center" bgcolor="background.paper" borderTop={1} borderColor="divider">
        <Typography variant="caption" color="textSecondary">
          <DashboardIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
          Dashboard: {selectedDashboard} | Time Range: Last {timeRange}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Edgentik Metrics Engine
        </Typography>
      </Box>
    </Box>
  );
};

export default GrafanaDashboards;
